/**
 * Role definitions shared by the browser and the Express server.
 *
 * This module deliberately has NO imports. `src/lib/db.ts` used to own these
 * declarations, but it also imports the browser Firebase SDK — so anything on
 * the server that needed `normalizeRole` dragged `firebase/app`,
 * `firebase/auth` and `firebase/firestore` into the server bundle and ran
 * `initializeApp()` in Node. Keeping the role vocabulary here severs that link.
 */

// Role tiers: admin sees everything; editor sees everything except Admin Settings;
// sales sees sales views; warehouse gets Directory, Analytics, and Non-Floral LTL.
export type UserRole = 'admin' | 'editor' | 'sales' | 'warehouse';

export const USER_ROLES: UserRole[] = ['admin', 'editor', 'sales', 'warehouse'];

export interface CRMUser {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  createdAt: string;
  signature?: string;
}

/** Accounts created before the role tiers used 'user'/'crm_editor' — map them forward. */
export function normalizeRole(role: string | null | undefined): UserRole {
  if (role === 'admin' || role === 'warehouse' || role === 'editor' || role === 'crm_editor') {
    return (role === 'crm_editor' ? 'editor' : role) as UserRole;
  }
  return 'sales';
}

/**
 * Higher number = more privilege. Used only to pick deterministically when a
 * single email somehow has more than one crm_users row, so that a duplicate can
 * never silently escalate a user beyond what an admin already granted them.
 */
const ROLE_PRECEDENCE: Record<UserRole, number> = {
  warehouse: 0,
  sales: 1,
  editor: 2,
  admin: 3,
};

export function isAtLeast(role: UserRole, minimum: UserRole): boolean {
  return ROLE_PRECEDENCE[role] >= ROLE_PRECEDENCE[minimum];
}

/** The email domain permitted to sign in. */
export const ALLOWED_EMAIL_DOMAIN = 'armellini.com';

export function isAllowedEmailDomain(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.trim().toLowerCase().endsWith(`@${ALLOWED_EMAIL_DOMAIN}`);
}
